import { RefObject, useCallback, useRef, useState } from 'react';

import { useHeroToast } from '../../../hooks';
import { AdAsset, usePostAdAsset } from '../../../queries';
import { uploadToBytescale } from '../utils/bytescale';
import { ACCEPTED_IMAGE_TYPES, loadImage, MAX_UPLOAD_BYTES } from '../utils/image';

interface AssetUploadApi {
  inputRef: RefObject<HTMLInputElement | null>;
  accept: string;
  isUploading: boolean;
  openPicker: () => void;
  handleFiles: (files: FileList | null) => Promise<AdAsset[]>;
}

/** Wymiary czytamy lokalnie, jeszcze przed wysyłką — CDN ich nie zwraca. */
const measure = async (file: File): Promise<{ width: number; height: number }> => {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    return { width: image.naturalWidth, height: image.naturalHeight };
  } catch {
    // SVG bez atrybutów `width`/`height` nie da się zmierzyć — wstawimy je
    // wtedy w rozmiarze domyślnym, zamiast blokować wgranie pliku.
    return { width: 0, height: 0 };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Wgrywa zdjęcia do Bytescale (katalog `hotelspot-admin-ads`) i dopisuje je do
 * biblioteki generatora.
 *
 * Pliki celowo NIE zostają w przeglądarce jako data URL: scena kreacji jedzie
 * potem do Firestore'a, gdzie dokument ma limit 1 MiB, a i tak chcemy, żeby to
 * samo zdjęcie dało się użyć w kolejnej kreacji bez ponownego wgrywania.
 */
export const useAssetUpload = (): AssetUploadApi => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { showError, showWarning } = useHeroToast();
  const { mutateAsync: registerAsset } = usePostAdAsset();

  const handleFiles = useCallback(async (files: FileList | null): Promise<AdAsset[]> => {
    if (!files || files.length === 0) return [];

    const accepted = Array.from(files).filter((file) => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        showError(`Format ${file.type || 'nieznany'} nie jest obsługiwany`);
        return false;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        showError(`Plik ${file.name} przekracza 8 MB`);
        return false;
      }
      return true;
    });

    if (accepted.length === 0) return [];

    setIsUploading(true);
    let libraryFailed = false;

    try {
      const assets = await Promise.all(accepted.map(async (file) => {
        const [{ width, height }, uploaded] = await Promise.all([
          measure(file),
          uploadToBytescale(file, file.name),
        ]);

        const data = {
          url: uploaded.url,
          filePath: uploaded.filePath,
          name: file.name,
          mime: uploaded.mime,
          size: uploaded.size,
          width,
          height,
        };

        try {
          return await registerAsset({ data });
        } catch {
          // Plik jest już w CDN, więc nieudany zapis metryczki nie może
          // przepaść razem z nim: pozwalamy wstawić zdjęcie na planszę,
          // tylko nie zobaczymy go potem na liście.
          libraryFailed = true;
          return { id: uploaded.filePath, createdAt: null, ...data };
        }
      }));

      if (libraryFailed) {
        showWarning('Zdjęcie wgrane, ale nie trafiło do biblioteki. Wstawisz je teraz na planszę, po odświeżeniu zniknie z listy.');
      }

      return assets;
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się wgrać pliku');
      return [];
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [registerAsset, showError, showWarning]);

  return {
    inputRef,
    accept: ACCEPTED_IMAGE_TYPES.join(','),
    isUploading,
    openPicker: () => inputRef.current?.click(),
    handleFiles,
  };
};
